import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BarChart3, Eye, Users, Mail, MailOpen, LogOut, Trash2, RefreshCcw, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/site/Logo";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview"); // overview | submissions
  const [analytics, setAnalytics] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/submissions"),
      ]);
      setAnalytics(a.data);
      setSubmissions(s.data);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { if (user && user.role === "admin") load(); }, [user, load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-base text-white flex items-center justify-center">
        <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/60">Loading…</span>
      </div>
    );
  }
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const markRead = async (id, read) => {
    try {
      await api.patch(`/admin/submissions/${id}/read?read=${read}`);
      setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, read } : s)));
      if (selected?.id === id) setSelected({ ...selected, read });
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/admin/submissions/${id}`);
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-navy-base text-white grid grid-cols-1 md:grid-cols-[240px_1fr]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="border-r border-navy-border bg-navy-surface/40 flex flex-col justify-between p-6 md:min-h-screen">
        <div>
          <Logo size={36} />
          <div className="mt-12 flex flex-col gap-1">
            <SideItem
              active={tab === "overview"}
              onClick={() => setTab("overview")}
              icon={<BarChart3 size={16} strokeWidth={1.3} />}
              label="Overview"
              testId="tab-overview"
            />
            <SideItem
              active={tab === "submissions"}
              onClick={() => setTab("submissions")}
              icon={<Mail size={16} strokeWidth={1.3} />}
              label="Submissions"
              badge={analytics?.submissions_unread || 0}
              testId="tab-submissions"
            />
          </div>
        </div>
        <div className="flex flex-col gap-4 pt-6 border-t border-navy-border">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 truncate">{user.email}</div>
          <button
            onClick={doLogout}
            className="flex items-center gap-3 font-mono text-[11px] tracking-[0.25em] uppercase text-white/60 hover:text-gold transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="p-6 md:p-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="overline">Operations Portal</div>
            <h1 className="h-display text-3xl md:text-4xl mt-2">
              {tab === "overview" ? "Overview" : "Contact Submissions"}
            </h1>
          </div>
          <button
            onClick={load}
            disabled={busy}
            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/60 hover:text-gold transition-colors disabled:opacity-50"
            data-testid="admin-refresh"
          >
            <RefreshCcw size={14} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {tab === "overview" && (
          <Overview analytics={analytics} submissions={submissions} onSeeAll={() => setTab("submissions")} />
        )}
        {tab === "submissions" && (
          <Submissions
            submissions={submissions}
            selected={selected}
            setSelected={setSelected}
            markRead={markRead}
            remove={remove}
          />
        )}
      </main>
    </div>
  );
}

function SideItem({ active, onClick, icon, label, badge, testId }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-between gap-3 px-4 py-3 border-l-2 transition-colors ${
        active
          ? "border-gold text-white bg-navy-surface/70"
          : "border-transparent text-white/60 hover:text-white hover:bg-navy-surface/40"
      }`}
      data-testid={testId}
    >
      <span className="flex items-center gap-3">
        {icon}
        <span className="font-mono text-[11px] tracking-[0.25em] uppercase">{label}</span>
      </span>
      {badge ? (
        <span className="font-mono text-[10px] tracking-[0.1em] bg-gold text-navy-base px-2 py-[2px]">{badge}</span>
      ) : null}
    </button>
  );
}

function Metric({ label, value, icon, testId }) {
  return (
    <div className="border border-navy-border p-6 bg-navy-surface/40" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">{label}</span>
        <span className="text-gold">{icon}</span>
      </div>
      <div className="font-display text-4xl md:text-5xl mt-6 text-white leading-none">{value}</div>
    </div>
  );
}

function Overview({ analytics, submissions, onSeeAll }) {
  if (!analytics) {
    return <div className="text-white/50 font-mono text-[11px] tracking-[0.25em]">Loading analytics…</div>;
  }
  const pv = analytics.pageviews_by_day || [];
  const sb = analytics.submissions_by_day || [];
  const maxPv = Math.max(1, ...pv.map((d) => d.count));
  const maxSb = Math.max(1, ...sb.map((d) => d.count));

  const recent = (submissions || []).slice(0, 5);

  return (
    <div className="flex flex-col gap-10">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Total pageviews" value={analytics.total_pageviews} icon={<Eye size={16} />} testId="metric-pageviews" />
        <Metric label="Unique visitors · 7d" value={analytics.unique_visitors_7d} icon={<Users size={16} />} testId="metric-visitors" />
        <Metric label="Enquiries · total" value={analytics.submissions_total} icon={<Mail size={16} />} testId="metric-submissions" />
        <Metric label="Unread" value={analytics.submissions_unread} icon={<MailOpen size={16} />} testId="metric-unread" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Chart title="Pageviews · last 14 days" data={pv} max={maxPv} testId="chart-pageviews" />
        <Chart title="Enquiries · last 14 days" data={sb} max={maxSb} testId="chart-submissions" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-navy-border p-6" data-testid="top-paths">
          <div className="flex items-center justify-between mb-6">
            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/70">Top paths</span>
          </div>
          <div className="flex flex-col divide-y divide-navy-border">
            {(analytics.top_paths || []).length === 0 && (
              <div className="text-white/40 text-sm py-4">No pageviews recorded yet.</div>
            )}
            {(analytics.top_paths || []).map((p) => (
              <div key={p.path} className="flex justify-between py-3">
                <span className="font-mono text-[12px] text-white/80 truncate max-w-[70%]">{p.path}</span>
                <span className="font-mono text-[12px] text-gold">{p.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-navy-border p-6" data-testid="service-breakdown">
          <div className="flex items-center justify-between mb-6">
            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/70">Interest breakdown</span>
          </div>
          <div className="flex flex-col divide-y divide-navy-border">
            {(analytics.service_breakdown || []).length === 0 && (
              <div className="text-white/40 text-sm py-4">No submissions yet.</div>
            )}
            {(analytics.service_breakdown || []).map((s) => (
              <div key={s.service} className="flex justify-between py-3 gap-6">
                <span className="text-white/80 text-sm truncate">{s.service}</span>
                <span className="font-mono text-[12px] text-gold">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-navy-border">
        <div className="flex items-center justify-between px-6 py-5 border-b border-navy-border">
          <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/70">Recent enquiries</span>
          <button onClick={onSeeAll} className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/60 hover:text-gold flex items-center gap-1">
            See all <ChevronRight size={14} />
          </button>
        </div>
        <div className="flex flex-col divide-y divide-navy-border">
          {recent.length === 0 && (
            <div className="p-6 text-white/40 text-sm">No submissions yet.</div>
          )}
          {recent.map((s) => (
            <div key={s.id} className="grid grid-cols-1 md:grid-cols-4 items-center gap-4 px-6 py-4">
              <div>
                <div className="text-white text-sm">{s.name}</div>
                <div className="text-white/50 text-xs font-mono">{s.email}</div>
              </div>
              <div className="text-white/60 text-sm md:col-span-2 truncate">{s.subject || s.message}</div>
              <div className="text-white/40 text-xs font-mono md:text-right">
                {new Date(s.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Chart({ title, data, max, testId }) {
  return (
    <div className="border border-navy-border p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-8">
        <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/70">{title}</span>
        <span className="font-mono text-[10px] text-gold">MAX {max}</span>
      </div>
      <div className="flex items-end gap-1 h-32">
        {data.map((d) => {
          const h = (d.count / max) * 100;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-2 group">
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${Math.max(4, h)}%` }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="w-full bg-gold/70 group-hover:bg-gold transition-colors"
                title={`${d.date}: ${d.count}`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-3 font-mono text-[9px] text-white/40">
        <span>{data[0]?.date?.slice(5)}</span>
        <span>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
}

function Submissions({ submissions, selected, setSelected, markRead, remove }) {
  const list = useMemo(() => submissions, [submissions]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
      <div className="border border-navy-border" data-testid="submissions-list">
        <div className="px-6 py-4 border-b border-navy-border font-mono text-[11px] tracking-[0.28em] uppercase text-white/70">
          {list.length} enquiries
        </div>
        <div className="flex flex-col divide-y divide-navy-border max-h-[70vh] overflow-y-auto">
          {list.length === 0 && (
            <div className="p-8 text-white/40 text-sm">No submissions yet.</div>
          )}
          {list.map((s) => (
            <button
              key={s.id}
              onClick={() => { setSelected(s); if (!s.read) markRead(s.id, true); }}
              className={`text-left px-6 py-4 hover:bg-navy-surface/60 transition-colors ${
                selected?.id === s.id ? "bg-navy-surface/80 border-l-2 border-gold" : ""
              }`}
              data-testid={`submission-item-${s.id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {!s.read && <span className="h-2 w-2 bg-gold rounded-full" />}
                  <span className="text-white text-sm font-medium">{s.name}</span>
                </div>
                <span className="font-mono text-[10px] text-white/40">
                  {new Date(s.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="text-white/60 text-xs mt-2 truncate">{s.subject || s.message}</div>
              <div className="font-mono text-[10px] text-gold/70 mt-2">{s.service_interest || "General"}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="border border-navy-border min-h-[300px]" data-testid="submission-detail">
        {!selected ? (
          <div className="flex items-center justify-center h-full text-white/40 text-sm font-mono tracking-[0.15em] p-10">
            Select an enquiry to view details
          </div>
        ) : (
          <div className="p-8 flex flex-col gap-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="overline">{selected.service_interest || "General enquiry"}</div>
                <h3 className="h-display text-2xl mt-2">{selected.subject || "New enquiry"}</h3>
                <div className="font-mono text-[10px] text-white/40 mt-2">
                  {new Date(selected.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => markRead(selected.id, !selected.read)}
                  className="btn-ghost !py-2 !px-4 text-[10px]"
                  data-testid="submission-toggle-read"
                >
                  {selected.read ? "Mark unread" : "Mark read"}
                </button>
                <button
                  onClick={() => remove(selected.id)}
                  className="btn-ghost !py-2 !px-4 text-[10px] hover:!border-crimson hover:!text-crimson"
                  data-testid="submission-delete"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6 border-t border-navy-border">
              <DetailRow label="Name" value={selected.name} />
              <DetailRow label="Email" value={<a className="text-gold" href={`mailto:${selected.email}`}>{selected.email}</a>} />
              <DetailRow label="Phone" value={selected.phone || "—"} />
              <DetailRow label="Company" value={selected.company || "—"} />
            </div>

            <div className="pt-6 border-t border-navy-border">
              <div className="overline mb-4">Message</div>
              <p className="whitespace-pre-wrap text-white/80 leading-[1.85] border-l border-gold/50 pl-6">
                {selected.message}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">{label}</div>
      <div className="text-white/90 text-sm break-words">{value}</div>
    </div>
  );
}
